// src/components/features/Interior/components/InteriorRoomWizard.jsx
// Per-room wizard — one question at a time
// Props: roomId, floorLabel, roomLabel, formData, setFormData, onNext, onBack, isLastStep, onSubmit
// display types:
//   "radio"        → (Tick preferable) — plain radio list
//   "radio_image"  → (Tick preferable with image) — swipe card + thumbnail row
//   "image_select" → (Will be shown with images) — image grid

import {
  Box, Flex, Text, Radio, Button, VStack, Badge, IconButton,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import { ROOM_QUESTIONS } from "../data/interiorRoomData";

// ─── Plain Radio ──────────────────────────────────────────────────────────────
function RadioQ({ question, value, onChange }) {
  return (
    <VStack spacing={2} align="stretch">
      {question.options.map((opt) => {
        const isSel = value === opt.id;
        return (
          <Box
            key={opt.id}
            border={isSel ? "2px solid" : "1px solid"}
            borderColor={isSel ? "cyan.500" : "gray.200"}
            borderRadius="10px" px={3} py={2}
            cursor="pointer" bg={isSel ? "cyan.50" : "white"}
            transition="all 0.15s" _hover={{ borderColor: "cyan.300" }}
            onClick={() => onChange(opt.id)}
          >
            <Flex align="center" gap={3}>
              <Radio value={opt.id} colorScheme="cyan" isChecked={isSel}
                onChange={() => onChange(opt.id)} size="md" />
              <Text fontSize="14px" fontWeight={isSel ? "600" : "400"}
                color={isSel ? "cyan.700" : "gray.700"}>
                {opt.label}
              </Text>
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );
}

// ─── Image Grid Select ────────────────────────────────────────────────────────
function ImageSelectQ({ question, value, onChange }) {
  return (
    <Flex gap="8px" flexWrap="wrap">
      {question.options.map((opt) => {
        const isSel = value === opt.id;
        return (
          <Box
            key={opt.id}
            cursor="pointer"
            border={isSel ? "2px solid" : "1.5px solid"}
            borderColor={isSel ? "cyan.500" : "gray.200"}
            borderRadius="10px"
            overflow="hidden"
            w="84px"
            bg={isSel ? "cyan.50" : "white"}
            transition="all 0.15s"
            _hover={{ borderColor: "cyan.300" }}
            onClick={() => onChange(opt.id)}
          >
            {opt.img ? (
              <Box
                h="58px"
                bgImage={`url(${opt.img})`}
                bgSize="cover"
                bgPos="center"
                position="relative"
              >
                {isSel && (
                  <>
                    <Box position="absolute" inset={0} bg="cyan.500" opacity={0.32} />
                    <Flex position="absolute" inset={0} align="center" justify="center">
                      <Box w="20px" h="20px" borderRadius="full" bg="cyan.500"
                        display="flex" alignItems="center" justifyContent="center">
                        <Text fontSize="11px" color="white" fontWeight="700">✓</Text>
                      </Box>
                    </Flex>
                  </>
                )}
              </Box>
            ) : (
              <Flex h="58px" bg="gray.50" align="center" justify="center">
                <Text fontSize="20px">✏️</Text>
              </Flex>
            )}
            <Box px={1} py={1.5}>
              <Text fontSize="10px" textAlign="center" lineHeight="1.3"
                color={isSel ? "cyan.700" : "gray.600"}
                fontWeight={isSel ? "600" : "400"}>
                {opt.label}
              </Text>
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
}

// ─── Swipe Card + Thumbnails ──────────────────────────────────────────────────
function SwipeCard({ opt, onLike, onDislike, current, total }) {
  const startX  = useRef(null);
  const [drag,  setDrag]  = useState(0);
  const [going, setGoing] = useState(null);
  const THRESHOLD = 65;
  const getX  = (e) => e.touches ? e.touches[0]?.clientX : e.clientX;
  const start = (e) => { startX.current = getX(e); };
  const move  = (e) => { if (startX.current === null) return; setDrag(getX(e) - startX.current); };
  const end   = () => {
    if      (drag > THRESHOLD)  { setGoing("right"); setTimeout(() => { setDrag(0); setGoing(null); onLike(); }, 250); }
    else if (drag < -THRESHOLD) { setGoing("left");  setTimeout(() => { setDrag(0); setGoing(null); onDislike(); }, 250); }
    else setDrag(0);
    startX.current = null;
  };
  const tx  = going === "left" ? -400 : going === "right" ? 400 : drag;
  const rot = Math.min(Math.max(drag / 14, -13), 13);

  return (
    <Box
      borderRadius="12px" overflow="hidden"
      border="1px solid" borderColor="gray.200"
      boxShadow="sm" cursor="grab" userSelect="none"
      style={{
        transform: `translateX(${tx}px) rotate(${rot}deg)`,
        opacity: going ? 0 : 1,
        transition: going ? "all 0.25s ease" : "transform 0.05s linear",
      }}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
    >
      {opt.img ? (
        <Box h="170px" bgImage={`url(${opt.img})`} bgSize="cover" bgPos="center" position="relative">
          {drag < -20 && (
            <Box position="absolute" top={3} left={3} bg="red.500" px={2} py={1} borderRadius="6px">
              <Text fontSize="11px" fontWeight="700" color="white">✕ Skip</Text>
            </Box>
          )}
          {drag > 20 && (
            <Box position="absolute" top={3} right={3} bg="cyan.500" px={2} py={1} borderRadius="6px">
              <Text fontSize="11px" fontWeight="700" color="white">✓ Like</Text>
            </Box>
          )}
          <Box position="absolute" top={2} right={2} bg="blackAlpha.600"
            px={2} py={0.5} borderRadius="20px">
            <Text fontSize="10px" color="white" fontWeight="600">{current} / {total}</Text>
          </Box>
          <Flex position="absolute" bottom={0} left={0} right={0}
            justify="space-between" align="center"
            px={3} py={2} bgGradient="linear(to-t, blackAlpha.700, transparent)">
            <Flex align="center" gap={1}>
              <Box w="17px" h="17px" borderRadius="full" bg="red.400"
                display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Text fontSize="9px" color="white" fontWeight="800">✕</Text>
              </Box>
              <Text fontSize="9px" color="whiteAlpha.900">Don't like</Text>
            </Flex>
            <Flex align="center" gap={1}>
              <Text fontSize="9px" color="whiteAlpha.900">Like</Text>
              <Box w="17px" h="17px" borderRadius="full" bg="cyan.400"
                display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Text fontSize="9px" color="white" fontWeight="800">✓</Text>
              </Box>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Flex h="90px" bg="gray.50" align="center" justify="center" direction="column" gap={1}>
          <Text fontSize="22px">✏️</Text>
          <Text fontSize="12px" color="gray.500">Designer Choice</Text>
        </Flex>
      )}
      <Box bg="white" px={3} py={2}>
        <Text fontSize="13px" fontWeight="600" color="gray.800">{opt.label}</Text>
      </Box>
    </Box>
  );
}

function RadioImageQ({ question, value, onChange }) {
  const withImg = question.options.filter((o) => o.img);
  const [ci, setCi]     = useState(0);
  const [liked, setLiked] = useState([]);
  const allDone = ci >= withImg.length;
  const current = withImg[ci];

  const handleLike = () => {
    const id = withImg[ci]?.id;
    if (!id) return;
    setLiked((l) => [...l, id]);
    onChange(id);
    setCi((i) => i + 1);
  };
  const handleDislike = () => setCi((i) => i + 1);

  return (
    <VStack spacing={3} align="stretch">
      {!allDone ? (
        <>
          <SwipeCard key={ci} opt={current} onLike={handleLike} onDislike={handleDislike}
            current={ci + 1} total={withImg.length} />
          <Flex gap={3} justify="center" align="center">
            <IconButton onClick={handleDislike} aria-label="skip"
              borderRadius="full" w="42px" h="42px"
              bg="red.50" color="red.500" border="1.5px solid" borderColor="red.200"
              _hover={{ bg: "red.100" }}
              icon={<Text fontSize="16px" lineHeight="1">✕</Text>} />
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              {ci + 1} / {withImg.length}
            </Text>
            <IconButton onClick={handleLike} aria-label="like"
              borderRadius="full" w="42px" h="42px"
              bg="cyan.50" color="cyan.600" border="1.5px solid" borderColor="cyan.300"
              _hover={{ bg: "cyan.100" }}
              icon={<Text fontSize="16px" lineHeight="1">✓</Text>} />
          </Flex>
        </>
      ) : (
        <Box bg="cyan.50" border="1px solid" borderColor="cyan.200"
          borderRadius="10px" px={3} py={2} textAlign="center">
          <Text fontSize="12px" color="cyan.700" fontWeight="600">
            ✓ All options reviewed — tap a thumbnail to select
          </Text>
        </Box>
      )}

      {/* Thumbnail row */}
      <Box>
        <Text fontSize="11px" color="gray.400" mb={2}>
          {allDone ? "Select from thumbnails" : "Or tap directly"}
        </Text>
        <Flex gap="6px" flexWrap="wrap">
          {question.options.map((opt) => {
            const isSel   = value === opt.id;
            const isLiked = liked.includes(opt.id);
            return (
              <Box key={opt.id}
                position="relative" w="52px" h="40px"
                borderRadius="7px" overflow="hidden"
                border={isSel ? "2px solid" : "1.5px solid"}
                borderColor={isSel ? "cyan.500" : isLiked ? "cyan.300" : "gray.200"}
                cursor="pointer" flexShrink={0}
                onClick={() => onChange(opt.id)}
                transition="all 0.15s" _hover={{ borderColor: "cyan.300" }}>
                {opt.img ? (
                  <Box w="100%" h="100%" bgImage={`url(${opt.img})`}
                    bgSize="cover" bgPos="center" />
                ) : (
                  <Flex w="100%" h="100%" bg="gray.50"
                    align="center" justify="center">
                    <Text fontSize="14px">✏️</Text>
                  </Flex>
                )}
                {isSel && (
                  <>
                    <Box position="absolute" inset={0} bg="cyan.500" opacity={0.38} />
                    <Flex position="absolute" inset={0} align="center" justify="center">
                      <Text fontSize="12px" color="white" fontWeight="800">✓</Text>
                    </Flex>
                  </>
                )}
                {isLiked && !isSel && (
                  <Box position="absolute" top="2px" right="2px"
                    w="6px" h="6px" borderRadius="full" bg="cyan.400" />
                )}
              </Box>
            );
          })}
        </Flex>
      </Box>

      {value && (
        <Box bg="cyan.50" border="1px solid" borderColor="cyan.200"
          borderRadius="8px" px={3} py={1.5}>
          <Text fontSize="12px" color="cyan.700" fontWeight="600">
            ✓ {question.options.find((o) => o.id === value)?.label}
          </Text>
        </Box>
      )}
    </VStack>
  );
}

// ─── Main Room Wizard ─────────────────────────────────────────────────────────
export default function InteriorRoomWizard({
  roomId, floorLabel, roomLabel,
  formData, setFormData,
  onNext, onBack, isLastStep, onSubmit,
  currentRoomStep,   // which question index (0-based)
  onRoomStepChange,  // (newIndex) => void
  viewMode,
}) {
  const questions   = ROOM_QUESTIONS[roomId] || [];
  const totalQ      = questions.length;
  const qIdx        = currentRoomStep || 0;
  const question    = questions[qIdx];

  const storageKey  = `${floorLabel}_${roomId}`;
  const roomAnswers = formData?.interiorRoomData?.[storageKey] || {};

  const setAnswer = (qId, val) => {
    setFormData({
      ...formData,
      interiorRoomData: {
        ...(formData.interiorRoomData || {}),
        [storageKey]: {
          ...roomAnswers,
          [qId]: val,
        },
      },
    });
  };

  const handleBack = () => {
    if (qIdx > 0) {
      onRoomStepChange(qIdx - 1);
    } else {
      onBack(); // go to previous room or step3
    }
  };

  const handleNext = () => {
    if (qIdx < totalQ - 1) {
      onRoomStepChange(qIdx + 1);
    } else {
      // last question of this room → go to next room or submit
      if (isLastStep) onSubmit();
      else onNext();
    }
  };

  const isLastQ      = qIdx === totalQ - 1;

  if (!question) return null;

  const currentAnswer = roomAnswers[question.id] || "";

  return (
    <Box
      h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between"
      px={4} py={6}
    >
      <Box
        flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        pointerEvents={viewMode ? "none" : "auto"}
        opacity={viewMode ? 0.6 : 1}
        css={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" } }}
      >
        <VStack spacing={4} align="stretch">
          {/* Room header */}
          <Box>
            <Flex align="center" justify="space-between" mb={1}>
              <Text fontSize="17px" fontWeight="600" color="gray.800">
                {roomLabel}
              </Text>
              <Badge colorScheme="cyan" fontSize="10px" variant="subtle">
                {floorLabel}
              </Badge>
            </Flex>
            <Text fontSize="12px" color="gray.500">
              Question {qIdx + 1} of {totalQ}
            </Text>
            {/* Question progress bar */}
            <Box bg="gray.200" borderRadius="full" h="2px" mt={2}>
              <Box
                bg="cyan.500" borderRadius="full" h="2px"
                w={`${((qIdx + 1) / totalQ) * 100}%`}
                transition="width 0.3s ease"
              />
            </Box>
          </Box>

          {/* Question label */}
          <Box>
            <Text fontSize="14px" fontWeight="600" color="gray.700" mb={3}>
              {question.label}
            </Text>

            {question.display === "radio" && (
              <RadioQ question={question} value={currentAnswer}
                onChange={(v) => setAnswer(question.id, v)} />
            )}
            {question.display === "image_select" && (
              <ImageSelectQ question={question} value={currentAnswer}
                onChange={(v) => setAnswer(question.id, v)} />
            )}
            {question.display === "radio_image" && (
              <RadioImageQ question={question} value={currentAnswer}
                onChange={(v) => setAnswer(question.id, v)} />
            )}
          </Box>
        </VStack>
      </Box>

      {/* Navigation */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={handleBack}
          variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500"
          borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext}
          variant="solid" bg="cyan.500" color="white"
          borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: "cyan.600" }}>
          {isLastStep && isLastQ ? "Submit" : isLastQ ? "Next Room →" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}